/**
 * contextSynthesizer.js
 *
 * Fusion engine: merges multiple tool results into a compact pastoral ministry
 * state object and synthesis string for LLM injection as [synthesis: ...].
 *
 * Used for broad queries ("¿cómo va el distrito?", "¿qué tengo esta semana?")
 * where a single tool result is insufficient.
 *
 * Inputs:
 *   results       — array of executeTool() outputs
 *   signals       — active session signals
 *   intent        — detected intent
 *   temporalIntent — from parseTemporalIntent()
 *   appScope      — role + district/church scope
 *
 * Outputs:
 *   ministryState — canonical district state object
 *   insights      — PastoralInsight[] sorted by severity
 *   compact       — "[synthesis: ...]" string for prompt injection, or null
 */

import { detectPastoralPatterns } from "./pastoralReasoningEngine";

const MAX_SYNTHESIS_CHARS = 500;

function groupByTool(results) {
  const map = {};
  for (const r of results ?? []) {
    if (r.ok && Array.isArray(r.rows) && r.rows.length > 0) {
      map[r.tool] = r.rows;
    }
  }
  return map;
}

const MONTHS_SHORT = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];
function _shortDate(iso) {
  if (!iso) return null;
  const [, m, d] = iso.split("-");
  return `${parseInt(d, 10)} ${MONTHS_SHORT[parseInt(m, 10) - 1]}`;
}

function buildCompact(ministryState, insights, preachingRows = []) {
  const {
    todayVisits, pendingVisits, upcomingEvents,
    activePrayers, followupPeople, activeChurches, topChurch,
    catechumens,
  } = ministryState;

  const parts = [];
  if (todayVisits > 0)    parts.push(`${todayVisits} visita${todayVisits !== 1 ? "s" : ""} hoy`);
  if (pendingVisits > 0)  parts.push(`${pendingVisits} pendiente${pendingVisits !== 1 ? "s" : ""} esta semana`);
  if (upcomingEvents > 0) parts.push(`${upcomingEvents} evento${upcomingEvents !== 1 ? "s" : ""} próximo${upcomingEvents !== 1 ? "s" : ""}`);
  if (activePrayers > 0)  parts.push(`${activePrayers} oración${activePrayers !== 1 ? "es" : ""} activa${activePrayers !== 1 ? "s" : ""}`);
  if (followupPeople > 0) parts.push(`${followupPeople} persona${followupPeople !== 1 ? "s" : ""} sin seguimiento`);
  if (activeChurches > 0) parts.push(`${activeChurches} iglesia${activeChurches !== 1 ? "s" : ""} activa${activeChurches !== 1 ? "s" : ""}`);
  if (catechumens > 0)    parts.push(`${catechumens} candidato${catechumens !== 1 ? "s" : ""} al bautismo`);

  let compact = parts.join(", ");
  if (compact) {
    compact += ".";
    if (topChurch && activeChurches > 1) {
      compact += ` Mayor actividad: ${topChurch}.`;
    }
  }

  // Next preaching from roster
  if (preachingRows.length > 0) {
    const p = preachingRows[0];
    const date    = _shortDate(p.date) ?? "—";
    const church  = p.church_name   || "iglesia";
    const preacher = p.preacher_name || "sin asignar";
    compact += ` Próxima predicación: ${date} — ${church}: ${preacher}.`;
  }

  // High-severity pastoral insights (max 2)
  const highPriority = insights.filter((i) => i.severity === "high").slice(0, 2);
  if (highPriority.length > 0) {
    compact += " Atención: " + highPriority.map((i) => i.insight).join(" ");
  }

  if (!compact.trim()) return null;
  return `[synthesis: ${compact.slice(0, MAX_SYNTHESIS_CHARS)}]`;
}

/**
 * Synthesize tool results into ministry state + insights + compact string.
 *
 * @param {{
 *   results?: Array<{ ok: boolean, tool: string, rows: Object[] }>,
 *   signals?: Array,
 *   intent?: string,
 *   temporalIntent?: string|null,
 *   appScope?: Object
 * }} params
 * @returns {{ ministryState: Object, insights: import("./pastoralReasoningEngine").PastoralInsight[], compact: string|null }}
 */
export function synthesizeContext({
  results       = [],
  signals       = [],
  intent        = "general",
  temporalIntent = null,
  appScope      = {},
} = {}) {
  const dataByTool = groupByTool(results);

  // Collect typed rows from each possible tool — fall back to dashboard snapshot rows
  const dashRows    = dataByTool["get_dashboard_snapshot"] ?? [];
  const dashVisits  = dashRows.filter((r) => r._source === "visit");
  const dashEvents  = dashRows.filter((r) => r._source === "event");
  const dashPrayers = dashRows.filter((r) => r._source === "prayer");

  const todayVisitRows    = dataByTool["get_today_visits"]   ?? dataByTool["get_next_visit"] ?? dashVisits;
  const upcomingVisitRows = dataByTool["get_upcoming_visits"] ??
    (dataByTool["get_week_schedule"] ?? []).filter((r) => r._source === "visit");
  const upcomingEventRows = dataByTool["get_upcoming_events"] ??
    (dataByTool["get_week_schedule"] ?? []).filter((r) => r._source === "event") ??
    dashEvents;
  const prayerRows        = dataByTool["get_recent_prayers"] ?? dashPrayers;
  const followupRows      = dataByTool["get_people_needing_followup"] ?? [];
  const churchRows        = dataByTool["get_church_activity_summary"] ?? [];
  const memberRows        = dataByTool["get_member_status"]  ?? [];

  const preachingRows = dataByTool["get_upcoming_preachings"] ?? [];

  const ministryState = {
    todayVisits:    todayVisitRows.length,
    pendingVisits:  upcomingVisitRows.filter((v) => v.status === "pendiente").length,
    upcomingEvents: upcomingEventRows.length,
    activePrayers:  prayerRows.filter((p) => p.status !== "respondida").length,
    followupPeople: followupRows.length,
    activeChurches: churchRows.filter((c) => c.status === "activa").length,
    topChurch:      churchRows[0]?.name ?? null,
    memberCount:    memberRows.length,
    catechumens:    memberRows.filter((m) => m.baptism_status === "catecumeno").length,
  };

  // Run pastoral reasoning on merged data
  const patternData = {
    ...dataByTool,
    "get_today_visits":   todayVisitRows,
    "get_recent_prayers": prayerRows,
  };
  const insights = detectPastoralPatterns(patternData);

  const compact = buildCompact(ministryState, insights, preachingRows);

  return { ministryState, insights, compact };
}
